import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { X, Cookie, Settings, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslate } from '@/hooks/useTranslate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function CookieConsentBanner() {
  const { t } = useTranslate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Check if consent already given
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setShow(true), 1000);
      return () => clearTimeout(timer);
    } else {
      try {
        const parsed = JSON.parse(consent);
        setPreferences(p => parsed.preferences || p);
      } catch (e) {
        // Invalid stored consent, show banner
        setShow(true);
      }
    }
  }, []);

  const getSessionId = () => {
    let sessionId = localStorage.getItem('cookie_session_id');
    if (!sessionId) {
      sessionId = uuidv4();
      localStorage.setItem('cookie_session_id', sessionId);
    }
    return sessionId;
  };

  const saveConsent = async (prefs) => {
    setSaving(true);
    const sessionId = getSessionId();
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      await fetch(`${BACKEND_URL}/api/gdpr/cookie-consent`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          session_id: sessionId,
          ...prefs
        })
      });
    } catch (err) {
      console.error('Failed to save cookie consent:', err);
    }

    // Store locally regardless of API success
    localStorage.setItem('cookie_consent', JSON.stringify({
      timestamp: new Date().toISOString(),
      preferences: prefs
    }));

    setSaving(false);
    setShow(false);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const rejectNonEssential = () => {
    const onlyEssential = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    setPreferences(onlyEssential);
    saveConsent(onlyEssential);
  };

  const saveCustom = () => {
    saveConsent(preferences);
  };

  if (location.pathname.startsWith('/widget/')) return null;

  if (!show) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 md:p-6" data-testid="cookie-consent-banner">
      <div className="max-w-4xl mx-auto bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="p-4 md:p-6 border-b dark:border-slate-700 bg-gradient-to-r from-slate-50 dark:from-slate-800 to-white dark:to-slate-800">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#2596BE]/10 rounded-lg">
                <Cookie className="w-6 h-6 text-[#2596BE]" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-slate-100">{t("Cookie Consent")}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{t("We value your privacy")}</p>
              </div>
            </div>
            <button 
              onClick={rejectNonEssential}
              className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4 md:p-6">
          <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
            {t("We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking 'Accept All', you consent to our use of cookies.")}
          </p>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Button 
              onClick={acceptAll}
              className="bg-[#2596BE] hover:bg-[#1e7a9a] text-white"
              disabled={saving}
              data-testid="accept-all-cookies"
            >
              {saving ? t("Saving...") : t("Accept All")}
            </Button>
            <Button 
              variant="outline" 
              onClick={rejectNonEssential}
              disabled={saving}
              data-testid="reject-cookies"
            >
              {t("Reject Non-Essential")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowDetails(!showDetails)}
              className="ml-auto"
              data-testid="customize-cookies"
            >
              <Settings className="w-4 h-4 mr-2" />
              {t("Customize")}
              {showDetails ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </Button>
          </div>

          {/* Detailed Preferences */}
          {showDetails && (
            <div className="space-y-4 pt-4 border-t dark:border-slate-700">
              {/* Essential Cookies */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">{t("Essential Cookies")}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{t("Required for the website to function properly")}</p>
                  </div>
                </div>
                <Switch checked={true} disabled className="data-[state=checked]:bg-green-600" />
              </div>

              {/* Functional Cookies */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{t("Functional Cookies")}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t("Remember your preferences and settings")}</p>
                </div>
                <Switch 
                  checked={preferences.functional}
                  onCheckedChange={(checked) => setPreferences(p => ({ ...p, functional: checked }))}
                  data-testid="functional-cookies-toggle"
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{t("Analytics Cookies")}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t("Help us understand how visitors use our site")}</p>
                </div>
                <Switch 
                  checked={preferences.analytics}
                  onCheckedChange={(checked) => setPreferences(p => ({ ...p, analytics: checked }))}
                  data-testid="analytics-cookies-toggle"
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-100">{t("Marketing Cookies")}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t("Used to deliver relevant advertisements")}</p>
                </div>
                <Switch 
                  checked={preferences.marketing}
                  onCheckedChange={(checked) => setPreferences(p => ({ ...p, marketing: checked }))}
                  data-testid="marketing-cookies-toggle"
                />
              </div>

              <Button 
                onClick={saveCustom} 
                className="w-full bg-[#2596BE] hover:bg-[#1e7a9a] text-white"
                disabled={saving}
                data-testid="save-cookie-preferences"
              >
                {saving ? t("Saving...") : t("Save Preferences")}
              </Button>
            </div>
          )}

          {/* Footer Links */}
          <div className="mt-4 pt-4 border-t dark:border-slate-700 flex flex-wrap gap-4 text-xs text-slate-500 dark:text-slate-400">
            <a href="/privacy" className="hover:text-[#2596BE] transition-colors">{t("Privacy Policy")}</a>
            <a href="/cookie-policy" className="hover:text-[#2596BE] transition-colors">{t("Cookie Policy")}</a>
            <a href="/terms" className="hover:text-[#2596BE] transition-colors">{t("Terms of Service")}</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function to check if user has consented to a specific cookie type
export function hasConsent(type) {
  const consent = localStorage.getItem('cookie_consent');
  if (!consent) return false;
  
  try {
    const parsed = JSON.parse(consent);
    return parsed.preferences?.[type] === true;
  } catch {
    return false;
  }
}

// Helper function to update cookie consent from settings
export function openCookieSettings() {
  localStorage.removeItem('cookie_consent');
  window.location.reload();
}